import { Button, Divider, Grid, Space, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { FaRegCheckCircle } from 'react-icons/fa';
import { FaRegClock } from 'react-icons/fa6';
import { IoArchiveOutline } from 'react-icons/io5';
import { LiaUserCogSolid } from 'react-icons/lia';
import { LuLayoutDashboard } from 'react-icons/lu';
import { MdAddTask } from 'react-icons/md';
import { RiListCheck3 } from 'react-icons/ri';
import { createSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import AddShop from '../../components/AddShop';
import CreateTask from '../../components/CreateTask';
import PageHeader from '../../components/PageHeader';
import UserSettingsModal from '../../components/RestaurantUsersModal';
import TaskDetailView from '../../components/TaskDetailView';
import ArchivedTask from '../../components/TaskTabs/ArchivedTask';
import CompletedTask from '../../components/TaskTabs/CompletedTask';
import CurrentTasks from '../../components/TaskTabs/CurrentTasks';
import ScheduledTask from '../../components/TaskTabs/ScheduledTask';
import TasksDashboard from '../../components/TaskTabs/TasksDashboard';
import UserGroup from '../../components/UserGroup';
import { TASk_TABS } from '../../constants';
import { getRoutes } from '../../constants/Routes';
import { isAdmin, isManager, isStaff, isSuperAdmin } from '../../helper';
import { decodeId } from '../../helper/hashids';
import useRestaurants from '../../hooks/useRestaurants';
import { getLabels } from '../../redux/actions/label';
import { getRestaurantUsers } from '../../redux/actions/restaurant';
import { getTaskById } from '../../redux/actions/tasks';

const { useBreakpoint } = Grid;

const RestaurantDetail = () => {
  const navigate = useNavigate();
  const { id: hashedId } = useParams();
  const id = decodeId(hashedId);
  const { restaurants, users } = useRestaurants();
  const location = useLocation();
  const queryParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const restaurant = restaurants.find((rest) => rest.id === parseInt(id));
  const taskId = queryParams.get('taskId');
  const [activeTab, setActiveTab] = useState(queryParams.get('tab') || TASk_TABS.DASHBOARD);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [isRestaurantModal, setRestaurantModal] = useState(false);
  const [taskData, setTaskData] = useState();

  const screens = useBreakpoint();
  const mediumScreen = !screens.md;

  const items = [
    {
      key: TASk_TABS.DASHBOARD,
      label: 'Dashboard',
      icon: <LuLayoutDashboard />,
      children: activeTab === TASk_TABS.DASHBOARD && <TasksDashboard />,
    },
    {
      key: TASk_TABS.PENDING,
      label: 'Pending Tasks',
      icon: <RiListCheck3 />,
      children: activeTab === TASk_TABS.PENDING && <CurrentTasks restaurant={restaurant} />,
    },
    {
      key: TASk_TABS.COMPLETED,
      label: 'Completed Tasks',
      icon: <FaRegCheckCircle />,
      children: activeTab === TASk_TABS.COMPLETED && <CompletedTask restaurant={restaurant} />,
    },
    {
      key: TASk_TABS.SCHEDULED,
      label: 'Scheduled Tasks',
      icon: <FaRegClock />,
      children: activeTab === TASk_TABS.SCHEDULED && <ScheduledTask restaurant={restaurant} />,
    },
    {
      key: TASk_TABS.ARCHIVED,
      label: 'Archived Tasks',
      icon: <IoArchiveOutline />,
      children: activeTab === TASk_TABS.ARCHIVED && <ArchivedTask restaurant={restaurant} />,
    },
  ];

  const handleTabChange = async (key) => {
    navigate({
      pathname: getRoutes().restaurant_detail.route(id),
      search: createSearchParams({
        tab: key,
      }).toString(),
    });
    setActiveTab(key);
  };

  const handleRestaurantEdit = () => {
    setRestaurantModal(true);
  };

  const getTask = async (taskId) => {
    const res = await getTaskById({ taskId });
    if (res.ack) {
      setTaskData(res?.data);
    }
  };

  useEffect(() => {
    if (!restaurant) {
      navigate('/');
    }
    getRestaurantUsers(id);
    getLabels({ id });
  }, [id]);

  useEffect(() => {
    const currentTab = queryParams.get('tab') || TASk_TABS.DASHBOARD;
    setActiveTab(currentTab);
    if (taskId) getTask(taskId);
  }, [queryParams]);

  return (
    <div className="Site-Detail">
      <PageHeader
        detail={{
          name: restaurant?.title,
        }}
        tabs={items}
        onTabChange={handleTabChange}
        activeKey={activeTab}
        extra={
          <Space align="center" gap={10} split={<Divider type="vertical" style={{ background: '$border-color' }} />}>
            <UserGroup users={users} maxCount={mediumScreen ? 3 : 5} />
            {(isSuperAdmin() || isAdmin() || isManager()) && (
              <Tooltip title="Manage Users">
                <Button
                  shape="circle"
                  variant="outlined"
                  color="default"
                  onClick={() => setIsUsersModalOpen(true)}
                >
                  <LiaUserCogSolid />
                </Button>
              </Tooltip>
            )}
            {(isSuperAdmin() || isAdmin() || isManager() || (isStaff() && !!restaurant?.allow_staff_create_task)) && (
              <Button type="primary" icon={<MdAddTask />} onClick={() => setIsModalOpen(true)}>
                Add New Task
              </Button>
            )}
          </Space>
        }
        onEditClick={(isSuperAdmin() || isAdmin()) && handleRestaurantEdit}
      />
      {isModalOpen && (
        <CreateTask
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={restaurant?.name}
        />
      )}

      {isUsersModalOpen && (
        <UserSettingsModal
          open={isUsersModalOpen}
          onClose={() => setIsUsersModalOpen(false)}
          users={users}
          restaurant={restaurant}
          activeTab={activeTab}
        />
      )}

      {isRestaurantModal && (
        <AddShop
          title="Update Site Detail"
          open={isRestaurantModal}
          onClose={() => setRestaurantModal(false)}
          data={restaurant}
        />
      )}

      {taskData && (
        <TaskDetailView
          open={taskData}
          task={taskData}
          restaurant={restaurant}
          onClose={() => {
            setTaskData();
            navigate(
              {
                pathname: getRoutes().restaurant_detail.route(id),
                search: createSearchParams({
                  tab: activeTab,
                }).toString(),
              },
              { replace: true }
            );
          }}
          isView
        />
      )}
    </div>
  );
};

export default RestaurantDetail;
